import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listProjects, deleteProject } from "../lib/api";
import { useAuth, loginWithGoogle } from "../lib/auth";
import {
  FolderOpen,
  Plus,
  Trash2,
  Pencil,
  AlertTriangle,
  ThermometerSun,
  Home as HomeIcon,
  Zap,
  Layers,
  Search,
  MapPin,
  X,
  ShieldCheck,
  LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ZONE_COLORS, DEPARTEMENT_NAMES } from "../lib/climate";
import { toast } from "sonner";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");

  const refresh = async () => {
    if (!user) {
      setProjects([]);
      return;
    }
    setLoading(true);
    try {
      const list = await listProjects();
      setProjects(list);
    } catch (e) {
      console.error(e);
      if (e?.response?.status !== 401) {
        toast.error("Impossible de charger les projets");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.user_id]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le projet "${name}" ?`)) return;
    try {
      await deleteProject(id);
      toast.success("Projet supprimé");
      refresh();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const { availableDepts, availableCities } = useMemo(() => {
    const depts = new Map();
    const cities = new Set();
    for (const p of projects) {
      const d = p.inputs?.departement;
      if (d) depts.set(d, DEPARTEMENT_NAMES[d] || d);
      if (p.city) cities.add(p.city);
    }
    return {
      availableDepts: [...depts.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      availableCities: [...cities].sort((a, b) => a.localeCompare(b, "fr")),
    };
  }, [projects]);

  const filtered = projects.filter((p) => {
    if (deptFilter !== "all" && p.inputs?.departement !== deptFilter) return false;
    if (cityFilter !== "all" && p.city !== cityFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) ||
      (p.client_name || "").toLowerCase().includes(q) ||
      (p.postal_code || "").toLowerCase().includes(q) ||
      (p.city || "").toLowerCase().includes(q) ||
      (p.address || "").toLowerCase().includes(q)
    );
  });

  const activeFilters = (deptFilter !== "all" ? 1 : 0) + (cityFilter !== "all" ? 1 : 0);
  const clearFilters = () => {
    setDeptFilter("all");
    setCityFilter("all");
    setQuery("");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10" data-testid="dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden pac-card p-5 sm:p-8 mb-6 sm:mb-8 bg-grain">
        <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-blue-100 opacity-50 blur-3xl" />
        <div className="absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-indigo-100 opacity-50 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="pac-pill bg-blue-100 text-blue-800 font-mono-data">
              CEE BAR-TH-171 · 2024–2026
            </span>
            <span className="pac-pill bg-emerald-100 text-emerald-800">
              Conforme RGE
            </span>
            <span
              className="pac-pill bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold inline-flex items-center gap-1.5 shadow-sm ring-1 ring-blue-700/20"
              data-testid="badge-conforme-2026"
              title="Méthode et coefficients alignés sur BAR-TH-171 vA78.4 (1er janv. 2026) et DTU 65.16"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Conforme 2026
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 max-w-3xl leading-tight">
            Dimensionnez vos pompes à chaleur <span className="text-blue-600">au kW près</span>.
          </h1>
          <p className="mt-3 sm:mt-4 text-slate-600 text-sm sm:text-base max-w-2xl">
            Outil professionnel pour artisans RGE et thermiciens. Calcul des déperditions, ECS, ballon tampon et coefficient
            de sécurité — conforme à la fiche BAR-TH-171 et MaPrimeRénov'.
          </p>
          <div className="mt-5 sm:mt-6 flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate("/calculator")}
              data-testid="new-project-cta"
              className="bg-[#3b82f6] hover:bg-blue-600 text-white h-11 sm:h-12 px-5 sm:px-6 rounded-xl shadow-sm text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau dimensionnement
            </Button>
            <a
              href="#projets"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors"
              data-testid="view-projects-anchor"
            >
              <FolderOpen className="h-4 w-4" />
              Voir l'historique
            </a>
          </div>
        </div>
      </div>

      {/* Features quick row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
        {[
          { icon: HomeIcon, label: "Bâtiment", desc: "Surface, isolation, hauteur" },
          { icon: ThermometerSun, label: "Climat", desc: "Zones A–I, altitude" },
          { icon: Zap, label: "Émetteurs", desc: "PC, BT, HT" },
        ].map(({ icon: Icon, label, desc }, i) => (
          <div key={label} className="pac-card pac-card-hover p-4 flex sm:block items-center gap-4 sm:gap-0" data-testid={`feature-card-${i}`}>
            <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
              <Icon className="h-5 w-5" />
            </div>
            <div className="sm:mt-3">
              <div className="font-display font-semibold text-slate-900">{label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Projects list */}
      <section id="projets">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              Mes projets
            </h2>
            <p className="text-sm text-slate-500">
              {filtered.length === projects.length
                ? `${projects.length} dimensionnement${projects.length > 1 ? "s" : ""} enregistré${projects.length > 1 ? "s" : ""}`
                : `${filtered.length} / ${projects.length} projet${projects.length > 1 ? "s" : ""} affiché${filtered.length > 1 ? "s" : ""}`}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="filter-department">
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Tous les départements</SelectItem>
                {availableDepts.map(([code, name]) => (
                  <SelectItem key={code} value={code} data-testid={`filter-dept-${code}`}>
                    {code} · {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-full sm:w-44 bg-white" data-testid="filter-city">
                <SelectValue placeholder="Ville" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Toutes les villes</SelectItem>
                {availableCities.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`filter-city-${c}`}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative col-span-2 sm:w-56 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 bg-white border-slate-200"
                data-testid="search-projects"
              />
            </div>
            {(activeFilters > 0 || query) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-900 h-9 col-span-2 sm:col-auto"
                data-testid="clear-filters"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Réinitialiser
              </Button>
            )}
          </div>
        </div>

        {!user ? (
          <div className="pac-card p-10 text-center" data-testid="projects-anon">
            <div className="mx-auto h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h3 className="mt-4 font-display font-semibold text-slate-900 text-lg">
              Connectez-vous pour retrouver vos projets
            </h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
              L'utilisation du simulateur est libre. Pour <strong>enregistrer</strong>{" "}
              ou <strong>exporter en PDF</strong> votre dimensionnement, créez-vous un compte
              en quelques secondes avec Google.
            </p>
            <Button
              onClick={() => loginWithGoogle()}
              className="mt-5 bg-[#3b82f6] hover:bg-blue-600 text-white rounded-xl"
              data-testid="anon-login-btn"
            >
              <LogIn className="h-4 w-4 mr-2" /> Se connecter avec Google
            </Button>
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400">
              <span>ou</span>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate("/calculator")}
              className="mt-3 rounded-xl"
              data-testid="anon-try-calc-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Essayer le simulateur sans compte
            </Button>
          </div>
        ) : loading ? (
          <div className="pac-card p-10 text-center text-slate-500" data-testid="projects-loading">
            Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="pac-card p-12 text-center" data-testid="projects-empty">
            <div className="mx-auto h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Layers className="h-6 w-6" />
            </div>
            <h3 className="mt-4 font-display font-semibold text-slate-900">
              {query || activeFilters > 0 ? "Aucun résultat" : "Aucun projet pour le moment"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {query || activeFilters > 0
                ? "Essayez d'autres filtres ou réinitialisez la recherche."
                : "Créez votre premier dimensionnement."}
            </p>
            {!query && activeFilters === 0 && (
              <Button
                onClick={() => navigate("/calculator")}
                className="mt-5 bg-[#3b82f6] hover:bg-blue-600 text-white rounded-xl"
                data-testid="empty-state-create-cta"
              >
                <Plus className="h-4 w-4 mr-2" /> Créer un projet
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <div
                key={p.id}
                className="pac-card pac-card-hover p-5 flex flex-col"
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: ZONE_COLORS[p.inputs?.zone] || "#94a3b8" }}
                      />
                      <span className="text-xs font-mono-data text-slate-500 uppercase tracking-wider">
                        Zone {p.inputs?.zone} · {p.inputs?.altitude} m
                      </span>
                    </div>
                    <h3 className="mt-1.5 font-display text-lg font-semibold text-slate-900 truncate">
                      {p.name}
                    </h3>
                    {p.client_name && (
                      <p className="text-sm text-slate-500 truncate">{p.client_name}</p>
                    )}
                    {(p.city || p.inputs?.departement) && (
                      <p className="mt-0.5 text-xs text-slate-400 truncate flex items-center gap-1">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        {[p.city, p.inputs?.departement && `(${p.inputs.departement})`]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    )}
                  </div>
                  {p.results?.alerte_cycles_courts && (
                    <span title="Risque cycles courts">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    </span>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 py-3 border-y border-slate-100">
                  <Metric label="Puissance" value={`${p.results?.puissance_totale_kw}`} unit="kW" big />
                  <Metric label="Déperdit." value={`${p.results?.deperditions_kw}`} unit="kW" />
                  <Metric label="Ballon" value={`${p.results?.ballon_tampon_l}`} unit="L" />
                </div>

                <div className="mt-auto pt-4 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    {new Date(p.created_at).toLocaleDateString("fr-FR")}
                  </span>
                  <div className="flex gap-2">
                    <Link to={`/calculator/${p.id}`}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg h-8 border-slate-200"
                        data-testid={`edit-${p.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Ouvrir
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(p.id, p.name)}
                      data-testid={`delete-${p.id}`}
                      className="rounded-lg h-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value, unit, big }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`font-mono-data font-semibold text-slate-900 ${big ? "text-lg" : "text-sm"}`}>
          {value}
        </span>
        <span className="text-xs font-mono-data text-slate-500">{unit}</span>
      </div>
    </div>
  );
}
